//@ts-nocheck

import { normalizeTravelDate } from './travelDate';

const UNLIMITED_PLAN_LIMIT = 30;

export const getItemDepositAmount = (item) => {
  const depositamount = Number(item?.depositamount);
  const selectedQuantity = Number(item?.selectedQuantity);

  if (!isNaN(depositamount) && !isNaN(selectedQuantity)) {
    return depositamount * selectedQuantity;
  } else {
    return 0;
  }
};

export const getItemTotal = (item) => {
  const priceperperson = Number(item?.priceperperson);
  const selectedQuantity = Number(item?.selectedQuantity);

  if (!isNaN(priceperperson) && !isNaN(selectedQuantity)) {
    return priceperperson * selectedQuantity;
  } else {
    return 0;
  }
};

/**
 * Gets multiples of a number
 * @param {number} num - The number of passengers per plan
 * @param {number} limit - The maximum available number of passengers
 * @returns {number[]} - Array of the available number of passengers to build dropdown options
 */
export const getNumberMultiples = (num, limit) => {
  const multiples = [];

  if (num <= 0 || limit < 0) {
    return [0];
  }

  // Loop through numbers from 0 to the limit, checking for multiples
  for (let i = 0; i <= limit; i++) {
    if (i % num === 0) {
      multiples.push(i);
    }
  }

  return multiples;
};

/**
 * Checks if a plan has limited availability
 * Note: This function works for both pricing plans and add-ons since they share
 * the same data structure (maxavailable field).
 * @param {Object} plan - The booking plan or add-on to check
 * @returns {boolean} - True if the plan has limited availability, false otherwise
 */
export const planHasLimitedAvailability = (plan) => {
  return (
    !isNaN(parseInt(plan?.maxavailable)) && parseInt(plan?.maxavailable) > 0
  );
};

/**
 * Checks if a plan is sold out
 * Note: This function works for both pricing plans and add-ons since they share
 * the same data structure (maxavailable, available, sold fields). The backend
 * calculates 'available' as (maxavailable - sold) for both record types.
 * @param {Object} plan - The booking plan or add-on to check
 * @returns {boolean} - True if the plan has limited availability and is sold out, false otherwise
 */
export const planIsSoldOut = (plan) => {
  return (
    planHasLimitedAvailability(plan) &&
    plan.available !== null &&
    plan.available <= 0
  );
};

/**
 * Checks if a plan is past its booking deadline
 * Note: This function works for both pricing plans and add-ons since they share
 * the same data structure (lastbookdate field).
 * @param {Object} plan - The booking plan or add-on to check
 * @returns {boolean} - True if the plan is past its booking deadline, false otherwise
 */
export const planIsPastDeadline = (plan) => {
  // Make sure users can book until END OF DAY (23:59:59) on the lastbookdate
  if (plan.lastbookdate === null || plan.lastbookdate == '') {
    return false;
  }

  // Create a date object from the lastbookdate
  const lastBookDate = new Date(plan.lastbookdate);

  // Set the time to 23:59:59 (end of day)
  const endOfDay = new Date(
    lastBookDate.getUTCFullYear(),
    lastBookDate.getUTCMonth(),
    lastBookDate.getUTCDate(),
    23,
    59,
    59,
    999
  );

  // Compare with current date
  return new Date() > endOfDay;
};

export const tripHasDeparted = (tripStartDate, today = new Date()) => {
  const normalizedTripStartDate = normalizeTravelDate(tripStartDate);
  const normalizedToday = normalizeTravelDate(today);

  if (!normalizedTripStartDate || !normalizedToday) {
    return false;
  }

  return normalizedToday >= normalizedTripStartDate;
};

export const getBookingPlanDropdownOptions = (
  plan,
  tripCapacity = null,
  otherPlansTotal = 0,
  usedCapacity = 0
) => {
  // Max plans that can be booked is:
  //  0 if past deadline or sold out
  //  plan.available if it has limited availability
  //  30 if it has unlimited availability
  let maxPlansBookable = UNLIMITED_PLAN_LIMIT;
  if (planIsPastDeadline(plan) || planIsSoldOut(plan)) {
    maxPlansBookable = 0;
  } else if (planHasLimitedAvailability(plan)) {
    maxPlansBookable = parseInt(plan?.available);
  } else {
    maxPlansBookable = UNLIMITED_PLAN_LIMIT;
  }

  const planOccupancy = parseInt(plan?.occupancy);

  // Calculate max passengers considering trip capacity
  let maxPassengers = maxPlansBookable * planOccupancy;

  // If trip capacity is set, apply capacity constraint
  if (
    tripCapacity !== null &&
    tripCapacity !== undefined &&
    !isNaN(tripCapacity)
  ) {
    // Available capacity = total capacity - already booked - selected in other plans
    const availableCapacity = tripCapacity - usedCapacity - otherPlansTotal;
    // Current plan's selection - we need to include this so users can see their current selection
    // and reduce it if needed, but not exceed the available capacity
    const currentPlanSelection = Number(plan?.selectedQuantity) || 0;

    // Max passengers is the greater of:
    // 1. Available capacity (for new selections)
    // 2. Current selection (to allow keeping/reducing current selection)
    maxPassengers = Math.min(
      maxPassengers,
      Math.max(availableCapacity, currentPlanSelection)
    );
  }

  // Note available and maxPlanAvailable are number of PLANS, not passengers
  //  We need to multiply by occupancy to build the dropdown options
  if (!isNaN(maxPlansBookable) && !isNaN(planOccupancy)) {
    const options = getNumberMultiples(planOccupancy, maxPassengers);

    // Check if minimum occupancy exceeds available capacity
    const minExceedsCapacity =
      tripCapacity !== null &&
      tripCapacity !== undefined &&
      planOccupancy > tripCapacity - usedCapacity - otherPlansTotal;

    return options.map((option) => {
      const isMinimum = option === planOccupancy && option > 0;
      const exceedsCapacity = isMinimum && minExceedsCapacity;

      return {
        id: option.toString(),
        value: option.toString(),
        label: exceedsCapacity
          ? `${option} (exceeds capacity)`
          : option.toString(),
        disabled: exceedsCapacity
      };
    });
  }

  return [
    {
      id: '0',
      value: '0',
      label: '0'
    }
  ];
};

export const getPlanDepositAmount = (plan) => {
  const depositamount = Number(plan?.depositamount);
  const occupancy = Number(plan?.occupancy);

  if (!isNaN(depositamount) && !isNaN(occupancy)) {
    return depositamount * occupancy;
  } else {
    return 0;
  }
};

export const getPlanTotal = (plan) => {
  const priceperperson = Number(plan?.priceperperson);

  if (!isNaN(priceperperson)) {
    return priceperperson;
  } else {
    return 0;
  }
};

export const hasMixedPlans = (bookingPlans = []) => {
  const plansWithDeposit = bookingPlans?.filter(
    (plan) => plan?.depositamount > 0 && plan?.selectedQuantity > 0
  );

  const plansWithoutDeposit = bookingPlans?.filter(
    (plan) => !plan?.depositamount && plan?.selectedQuantity > 0
  );

  return plansWithDeposit.length > 0 && plansWithoutDeposit.length > 0;
};

export const getBookingTotals = (bookingPlans = []) => {
  let totalPrice = 0;
  let totalDeposit = 0;
  const selectedPlans = bookingPlans?.filter(
    (plan) => plan.selectedQuantity > 0
  );
  const includeMixedPlans = hasMixedPlans(selectedPlans);

  selectedPlans.forEach((tripPlan) => {
    const selectedQuantity = Number(tripPlan?.selectedQuantity);
    const priceperperson = Number(tripPlan?.priceperperson);
    const depositamount = Number(tripPlan?.depositamount);

    //Get pricing plan total
    if (!isNaN(priceperperson)) {
      totalPrice += priceperperson * selectedQuantity;
    }

    //Get pricing plan deposit amount
    if (includeMixedPlans) {
      if (!isNaN(depositamount) && depositamount > 0) {
        totalDeposit += depositamount * selectedQuantity;
      } else {
        totalDeposit += priceperperson * selectedQuantity;
      }
    } else {
      if (!isNaN(depositamount)) {
        totalDeposit += depositamount * selectedQuantity;
      }
    }
  });

  return {
    totalPrice,
    totalDeposit
  };
};

export const getPassengerName = (passenger) => {
  const firstName =
    passenger?.passengerfields?.find((field) => field?.title === 'First Name')
      ?.options[0]?.value || '';
  const lastName =
    passenger?.passengerfields?.find((field) => field?.title === 'Last Name')
      ?.options[0]?.value || '';
  return `${firstName} ${lastName}`;
};

/**
 * Calculates the amount to charge based on payment option
 * @param {string} paymentOption - The selected payment option ('optiondeposit', 'optionfullamount', 'optionnopayment')
 * @param {number} totalPrice - The full trip price
 * @param {number} totalDeposit - The total deposit amount
 * @returns {number} - The amount to charge the user now
 */
export const calculateAmountToCharge = (
  paymentOption,
  totalPrice,
  totalDeposit
) => {
  switch (paymentOption) {
    case 'optiondeposit':
      return totalDeposit || 0;
    case 'optionfullamount':
      return totalPrice || 0;
    case 'optionnopayment':
      return 0;
    default:
      return 0;
  }
};

/**
 * Calculate total passengers selected across all booking plans
 * @param {Array} bookingPlans - Array of booking plans
 * @returns {number} - Total number of passengers selected
 */
export const getTotalSelectedPassengers = (bookingPlans = []) => {
  return bookingPlans.reduce((total, plan) => {
    const selectedQuantity = Number(plan?.selectedQuantity) || 0;
    return total + selectedQuantity;
  }, 0);
};

/**
 * Calculate remaining trip capacity
 * @param {number|null} tripCapacity - Total trip capacity (null means unlimited)
 * @param {Array} bookingPlans - Array of booking plans
 * @param {number} usedCapacity - Passengers already booked on this trip
 * @returns {number|null} - Remaining capacity (null if unlimited)
 */
export const getRemainingCapacity = (
  tripCapacity,
  bookingPlans = [],
  usedCapacity = 0
) => {
  if (tripCapacity === null || tripCapacity === undefined) {
    return null;
  }
  const totalSelected = getTotalSelectedPassengers(bookingPlans);
  return Math.max(0, tripCapacity - usedCapacity - totalSelected);
};

/**
 * Check if trip capacity is exceeded
 * @param {number|null} tripCapacity - Total trip capacity
 * @param {Array} bookingPlans - Array of booking plans
 * @param {number} usedCapacity - Passengers already booked on this trip
 * @returns {boolean} - True if capacity is exceeded
 */
export const isCapacityExceeded = (
  tripCapacity,
  bookingPlans = [],
  usedCapacity = 0
) => {
  if (tripCapacity === null || tripCapacity === undefined) {
    return false;
  }
  const totalSelected = getTotalSelectedPassengers(bookingPlans);
  return totalSelected > tripCapacity - usedCapacity;
};

/**
 * Check if a plan's minimum occupancy exceeds remaining capacity
 * @param {Object} plan - The booking plan
 * @param {number|null} remainingCapacity - Remaining trip capacity
 * @returns {boolean} - True if minimum exceeds capacity
 */
export const planMinExceedsCapacity = (plan, remainingCapacity) => {
  if (remainingCapacity === null || remainingCapacity === undefined) {
    return false;
  }
  const minOccupancy = parseInt(plan?.occupancy) || 0;
  return minOccupancy > remainingCapacity;
};

/**
 * Check if a plan should be disabled based on capacity constraints
 * Disables only if: selectedQuantity is 0 AND remaining capacity (after other selections) < occupancy
 * @param {Object} plan - The booking plan
 * @param {number|null} tripCapacity - Total trip capacity
 * @param {number} usedCapacity - Passengers already booked on this trip
 * @param {number} otherPlansTotal - Total passengers selected in OTHER plans (excluding this one)
 * @returns {boolean} - True if plan should be disabled
 */
export const planOccupancyExceedsAvailableCapacity = (
  plan,
  tripCapacity,
  usedCapacity = 0,
  otherPlansTotal = 0
) => {
  if (tripCapacity === null || tripCapacity === undefined) {
    return false;
  }
  const planOccupancy = parseInt(plan?.occupancy) || 0;
  const selectedQuantity = Number(plan?.selectedQuantity) || 0;

  // Only check if user hasn't selected this plan yet
  if (selectedQuantity > 0) {
    return false;
  }

  // Calculate remaining capacity after accounting for already booked + other user selections
  const remainingCapacity = tripCapacity - usedCapacity - otherPlansTotal;

  // Disable if this plan's occupancy won't fit in remaining capacity
  return planOccupancy > remainingCapacity;
};
